export const LESSON_COMPLETION_THRESHOLD = 90;

export function clampPercentage(value) {
  if (!Number.isFinite(Number(value))) return 0;
  return Math.min(100, Math.max(0, Math.round(Number(value))));
}

export function deriveLessonState(progressPercentage, completedAt = null) {
  const percentage = completedAt ? 100 : clampPercentage(progressPercentage);
  if (completedAt || percentage >= LESSON_COMPLETION_THRESHOLD) return 'COMPLETED';
  if (percentage > 0) return 'IN_PROGRESS';
  return 'NOT_STARTED';
}

export function aggregateLessonProgress(lessons) {
  if (!lessons.length) return { totalLessons: 0, completedLessons: 0, progressPercentage: 0 };
  const percentages = lessons.map((lesson) =>
    deriveLessonState(lesson.progressPercentage, lesson.completedAt) === 'COMPLETED'
      ? 100
      : clampPercentage(lesson.progressPercentage)
  );
  return {
    totalLessons: lessons.length,
    completedLessons: percentages.filter((value) => value === 100).length,
    progressPercentage: Math.round(percentages.reduce((sum, value) => sum + value, 0) / lessons.length),
  };
}

export function mergeMonotonicProgress(existing, incoming) {
  const progressPercentage = Math.max(
    clampPercentage(existing?.progressPercentage ?? 0),
    clampPercentage(incoming.progressPercentage)
  );
  const completed = Boolean(existing?.completedAt) || progressPercentage >= LESSON_COMPLETION_THRESHOLD;
  return {
    progressPercentage,
    lastPositionSeconds: Math.max(0, Number(existing?.lastPositionSeconds ?? 0), Number(incoming.lastPositionSeconds ?? 0)),
    watchedSeconds: Math.max(0, Number(existing?.watchedSeconds ?? 0)) + Math.max(0, Number(incoming.watchedSecondsDelta ?? 0)),
    completedAt: completed ? (existing?.completedAt ?? incoming.completedAt ?? new Date().toISOString()) : null,
  };
}

export function formatDuration(durationSeconds) {
  const total = Math.max(0, Math.round(Number(durationSeconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function parseLegacyDuration(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const normalized = String(value).trim();
  if (/^\d+$/.test(normalized)) {
    const seconds = Number(normalized);
    return seconds >= 300 ? seconds : null;
  }
  const parts = normalized.split(':').map(Number);
  if (parts.some((part) => !Number.isInteger(part) || part < 0)) return null;
  if (parts.length === 2 && parts[1] < 60) return parts[0] * 60 + parts[1];
  if (parts.length === 3 && parts[1] < 60 && parts[2] < 60) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}
